import Icon from 'components/Icon';
import * as consts from 'consts/consts';
import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Card, CardBody } from 'reactstrap';
import { type Person, type ReduxState } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { parseAndFormatQuestionResponse } from 'utils/models/Performance';
import { getPrettyDate, stripHtml } from 'utils/util/util';
import {
  extractSurveyAnswerResponse,
  noAnswerElement,
  type SurveyQuestionDefinition,
} from 'views/CustomSurveys/formatSurveyAnswer';
import Page from 'views/Layout/Pages/Page';
import AttachedContentTypeCommentsProvider from 'views/Widgets/Comments/AttachedContentTypeCommentsProvider';
import ThreadedCommentButton from 'views/Widgets/Comments/ThreadedCommentButton';
import { useThreadedCommentsByObjectId } from 'views/Widgets/Comments/ThreadedCommentsByObjectIdContext';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable, {
  FilterType,
} from 'views/Widgets/People/FilterablePeopleTable';
import { type CustomSurveyData, getQuestionTypeName } from './consts';
import {
  extractPersonFilterFields,
  personFilterOnlyColumns,
  personFilterSuggestionsGenerator,
} from './personFilters';

interface RouteParams {
  id: string;
  instanceId: string;
  uuid: string;
}

interface QuestionAnswerEntry {
  id: number;
  respondent: Person;
  was_shown: boolean;
  has_responded: boolean;
  answer: Record<string, unknown> | null;
  answer_id: number | null;
  created_at: string | null;
  can_comment: boolean;
}

interface QuestionAnswersResponse {
  question: {
    id: number;
    uuid: string;
    question: SurveyQuestionDefinition;
  };
  answers: QuestionAnswerEntry[];
}

interface CustomSurveyQuestionAnswersContentProps {
  data: QuestionAnswersResponse | undefined;
  columns: any[];
  respondedYesLabel: string;
  respondedNoLabel: string;
}

const CustomSurveyQuestionAnswersContent: FC<
  CustomSurveyQuestionAnswersContentProps
> = ({ data, columns, respondedYesLabel, respondedNoLabel }) => {
  const { formatMessage, locale } = useIntl();
  const customSurveyAnswerContentType = useSelector(
    (state: ReduxState) => state.attachedContentTypes.custom_survey_answer
  );
  const {
    commentsByObjectId,
    isLoading: commentsLoading,
    onCommentsChange,
  } = useThreadedCommentsByObjectId();

  const questionTitle = useMemo(() => {
    if (!data) {
      return '';
    }
    return stripHtml(data.question.question.label).trim();
  }, [data]);

  const renderCommentsCell = useCallback(
    (a: QuestionAnswerEntry) => {
      if (!a.answer_id) {
        return null;
      }
      const answerId = a.answer_id;
      return (
        <ThreadedCommentButton
          contentType={customSurveyAnswerContentType}
          objectId={answerId}
          canComment={a.can_comment}
          comments={commentsByObjectId?.[answerId] ?? []}
          onCommentsChange={(comments) => onCommentsChange(answerId, comments)}
          title={formatMessage(
            {
              id: 'app.views.administration.custom_surveys.question_answers.comments_modal.title',
              defaultMessage: 'Comments for {person} - “{question}”',
            },
            {
              person:
                a.respondent.full_name ||
                formatMessage({
                  id: 'app.views.administration.custom_surveys.question_answers.comments_modal.person_placeholder',
                  defaultMessage: 'this person',
                }),
              question:
                questionTitle ||
                formatMessage({
                  id: 'app.views.administration.custom_surveys.question_answers.comments_modal.question_placeholder',
                  defaultMessage: 'this question',
                }),
            }
          )}
          addCommentLabel={
            <FormattedMessage
              id="app.views.administration.custom_surveys.question_answers.comments_modal.add_comment_label"
              defaultMessage="Add a comment"
            />
          }
        />
      );
    },
    [
      customSurveyAnswerContentType,
      commentsByObjectId,
      onCommentsChange,
      formatMessage,
      questionTitle,
    ]
  );

  const rows = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.answers.map((a) => ({
      ...extractPersonFilterFields(a.respondent),
      id: a.id,
      person: a.respondent,
      shown: a.was_shown ? respondedYesLabel : respondedNoLabel,
      responded: a.has_responded ? respondedYesLabel : respondedNoLabel,
      formatted_response: a.has_responded ? (
        <div className="fr-view">
          {parseAndFormatQuestionResponse({
            q: {
              question: data.question.question,
              response: extractSurveyAnswerResponse(
                data.question.question.type,
                a.answer ?? undefined
              ),
            },
            noneElement: noAnswerElement,
            formatMessage,
            locale,
          })}
        </div>
      ) : null,
      submitted_at:
        a.has_responded && a.created_at
          ? getPrettyDate({
              dateString: a.created_at,
              includeTime: true,
              locale,
            })
          : '',
      comments: renderCommentsCell(a),
    }));
  }, [
    data,
    formatMessage,
    locale,
    respondedYesLabel,
    respondedNoLabel,
    renderCommentsCell,
  ]);

  if (!data || commentsLoading) {
    return <Loading />;
  }

  return (
    <>
      <Card className="mb-3">
        <CardBody>
          <h4 className="mb-2">
            <RichTextViewer
              model={data.question.question.label}
              expanded={true}
            />
          </h4>
          <div className="text-muted small">
            {getQuestionTypeName(data.question.question.type, formatMessage)}
          </div>
        </CardBody>
      </Card>
      {data.answers.length === 0 ? (
        <Card>
          <CardBody className="text-muted text-center">
            <FormattedMessage
              id="app.views.administration.custom_surveys.question_answers.no_answers"
              defaultMessage="No answers for this question yet."
            />
          </CardBody>
        </Card>
      ) : (
        <FilterablePeopleTable
          columns={columns}
          rows={rows}
          filterType={FilterType.ADVANCED}
          suggestionGenerator={personFilterSuggestionsGenerator}
          hideExportButton={true}
        />
      )}
    </>
  );
};

const CustomSurveyQuestionAnswersPage: FC = () => {
  const { id, instanceId, uuid } = useParams<RouteParams>();
  const { formatMessage } = useIntl();
  const history = useHistory();

  const { data: survey } = useConfirmApi<CustomSurveyData>({
    method: 'GET',
    url: `custom-survey/surveys/${id}`,
  });

  const { data } = useConfirmApi<QuestionAnswersResponse>({
    method: 'GET',
    url: `custom-survey/instances/${instanceId}/admin-question-answers/${uuid}`,
  });

  const answerIds = data?.answers.map((a) => a.answer_id) ?? [];

  const handleBack = useCallback(() => {
    history.push(
      `${consts.ADMINISTRATION_CUSTOM_SURVEYS_RESPONSES(formatMessage).path.replace(':id', id)}?instance=${instanceId}`
    );
  }, [history, formatMessage, id, instanceId]);

  const respondedYesLabel = formatMessage({
    id: 'app.views.administration.custom_surveys.question_answers.responded.yes',
    defaultMessage: 'Yes',
  });
  const respondedNoLabel = formatMessage({
    id: 'app.views.administration.custom_surveys.question_answers.responded.no',
    defaultMessage: 'No',
  });

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.question_answers.col.respondent',
          defaultMessage: 'Respondent',
        }),
        field: 'person',
        isPerson: true,
        sortable: false,
        getFilterDisplayValue: (p: Person) => p.full_name,
        fieldTransformer: (value, operator) =>
          `(${operator == 'EQ' ? '==' : '!='} "${
            value.filterObject.value
          }" person.id)`,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.question_answers.col.shown',
          defaultMessage: 'Shown',
        }),
        field: 'shown',
        className: 'col-2',
        sortable: false,
        format: (value: string) =>
          value === respondedYesLabel ? (
            value
          ) : (
            <span className="text-muted">{value}</span>
          ),
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.question_answers.col.responded',
          defaultMessage: 'Responded',
        }),
        field: 'responded',
        className: 'col-2',
        sortable: false,
        format: (value: string) =>
          value === respondedYesLabel ? (
            value
          ) : (
            <span className="text-muted">{value}</span>
          ),
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.question_answers.col.formatted_response',
          defaultMessage: 'Response',
        }),
        field: 'formatted_response',
        sortable: false,
        hideFromFilters: true,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.question_answers.col.submitted',
          defaultMessage: 'Submitted',
        }),
        field: 'submitted_at',
        className: 'text-nowrap',
        sortable: false,
        hideFromFilters: true,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.question_answers.col.comments',
          defaultMessage: 'Comments',
        }),
        field: 'comments',
        className: 'text-nowrap',
        sortable: false,
        hideFromFilters: true,
      },
      // Hidden columns: extracted person attributes (manager, chain of command,
      // org attributes), used only for filtering.
      ...personFilterOnlyColumns(formatMessage),
    ],
    [formatMessage, respondedYesLabel]
  );

  return (
    <Page plainTextTitle={survey?.name ?? ''}>
      <>
        <div className="position-relative d-flex align-items-center mt-4 mb-4">
          <Button color="link" onClick={handleBack}>
            <Icon className="me-1" icon={consts.ICONS.ARROW_LEFT} />
            <FormattedMessage
              id="app.views.administration.custom_surveys.question_answers.back"
              defaultMessage="Back"
            />
          </Button>
          <h2 className="mb-0 position-absolute start-50 translate-middle-x">
            {survey?.name ?? ''}
          </h2>
        </div>
        <AttachedContentTypeCommentsProvider
          key={uuid}
          contentTypeKey="custom_survey_answer"
          objectIds={answerIds}
        >
          <CustomSurveyQuestionAnswersContent
            data={data}
            columns={columns}
            respondedYesLabel={respondedYesLabel}
            respondedNoLabel={respondedNoLabel}
          />
        </AttachedContentTypeCommentsProvider>
      </>
    </Page>
  );
};

export default CustomSurveyQuestionAnswersPage;
